import "core-js/modules/es.array.reverse.js";
var _class;
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
function _applyDecoratedDescriptor(i, e, r, n, l) { var a = {}; return Object.keys(n).forEach(function (i) { a[i] = n[i]; }), a.enumerable = !!a.enumerable, a.configurable = !!a.configurable, ("value" in a || a.initializer) && (a.writable = !0), a = r.slice().reverse().reduce(function (r, n) { return n(i, e, r) || r; }, a), l && void 0 !== a.initializer && (a.value = a.initializer ? a.initializer.call(l) : void 0, a.initializer = void 0), void 0 === a.initializer ? (Object.defineProperty(i, e, a), null) : a; }
import { action, computed, makeObservable } from "mobx";
import createGuid from "terriajs-cesium/Source/Core/createGuid";
import TerriaError from "../../Core/TerriaError";
import MapNavigationItemController from "./MapNavigationItemController";
export { default as ViewerMode } from "../../Models/ViewerMode";

/**
 * Adds a new tool to the map toolbar
 *
 * @param viewState The {@link ViewState} instance
 * @param config The tool configuration
 * @returns `id` of the tool. This will be the same as `config.id` if it was set. Otherwise, a random `id` is generated and returned.
 *
 * Example:
 * const toolId = addTool(viewState, {
 *   name: "X Tool",
 *   toolComponentLoader: () => import("./XTool.tsx"),
 *   toolButton: {
 *        text: "Open X tool",
 *        tooltip: "X Tool",
 *        icon: X_ICON
 *   }
 * })
 */
export function addTool(viewState, config) {
  var _config$id, _config$toolButton$se;
  const id = (_config$id = config.id) !== null && _config$id !== void 0 ? _config$id : createGuid();
  const controller = new ToolController(viewState, id, config);
  const terria = viewState.terria;
  terria.mapNavigationModel.addItem({
    id,
    name: config.toolButton.text,
    title: config.toolButton.tooltip,
    location: (_config$toolButton$se = config.toolButton.section) !== null && _config$toolButton$se !== void 0 ? _config$toolButton$se : "TOP",
    order: config.toolButton.order,
    controller
  });
  return id;
}

/**
 * Function to programatically open the tool with given ID.
 *
 * Note that in normal operation, a tool will be opened when user clicks the
 * tool button in the map toolbar. This function is useful in situations where
 * the tool needs to be opened through other means - like clicking a workbench
 * viewing-controls button.
 *
 * If no tool exists with the given `toolId` then this function does nothing
 * and returns `false`.  The caller can also check if the call was successful
 * by checking the result of {@link isToolOpen()}.
 *
 * @param viewState The `ViewState` instance.
 * @param toolId ID of the tool to open. See {@link addTool}.
 * @param props Optional props to pass to the Tool component when activating it.
 * @returns `true` if the tool was successfully opened.
 */
export function openTool(viewState, toolId, props) {
  const controller = findToolController(viewState, toolId);
  controller === null || controller === void 0 || controller.openTool(props);
  return (controller === null || controller === void 0 ? void 0 : controller.isToolOpen) === true;
}

/**
 * Closes the tool with given id.
 *
 * @param viewState The `ViewState` instance.
 * @param toolId ID of the tool to close.
 */
export function closeTool(viewState, toolId) {
  var _findToolController;
  (_findToolController = findToolController(viewState, toolId)) === null || _findToolController === void 0 || _findToolController.closeTool();
}

/**
 * Check if tool with given `id` is currently open.
 *
 * @param viewState The `ViewState` instance.
 * @param toolId ID of the tool to check.
 * @returns `true` if the tool is currently open, otherwise returns `false`.
 */
export function isToolOpen(viewState, toolId) {
  var _findToolController2;
  return ((_findToolController2 = findToolController(viewState, toolId)) === null || _findToolController2 === void 0 ? void 0 : _findToolController2.active) === true;
}

/**
 * Removes the tool with the given id from the toolbar.
 *
 * @param viewState The `ViewState` instance.
 * @param toolId ID of the tool to close.
 */
export function removeTool(viewState, toolId) {
  viewState.terria.mapNavigationModel.remove(toolId);
}

/**
 * Find `ToolController` by id.
 */
function findToolController(viewState, toolId) {
  const navItem = viewState.terria.mapNavigationModel.items.find(it => it.id === toolId);
  return (navItem === null || navItem === void 0 ? void 0 : navItem.controller) instanceof ToolController ? navItem.controller : undefined;
}
export let ToolController = (_class = class ToolController extends MapNavigationItemController {
  constructor(viewState, toolId, toolConfig) {
    super();
    this.viewState = viewState;
    this.toolId = toolId;
    this.toolConfig = toolConfig;
    makeObservable(this);
  }
  get glyph() {
    return this.toolConfig.toolButton.icon;
  }
  get viewerMode() {
    return this.toolConfig.viewerMode;
  }
  get isToolOpen() {
    var _this$viewState$curre;
    return ((_this$viewState$curre = this.viewState.currentTool) === null || _this$viewState$curre === void 0 ? void 0 : _this$viewState$curre.toolName) === this.toolConfig.name;
  }
  get active() {
    return super.active && this.isToolOpen;
  }
  openTool() {
    let props = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    const toolConfig = this.toolConfig;
    const toolId = this.toolId;
    try {
      this.viewState.openTool({
        toolName: toolConfig.name,
        getToolComponent: () => toolConfig.toolComponentLoader().then(m => m.default),
        params: _objectSpread(_objectSpread({}, props), {}, {
          // Pass toolId as an extra prop to the component.
          // TODO: Maybe we should use react contexts to do this instead of a magic prop?
          toolId
        })
      });
    } catch (err) {
      this.viewState.terria.raiseErrorToUser(TerriaError.from(err));
    }
    super.activate();
  }
  activate() {
    this.openTool();
  }
  deactivate() {
    this.closeTool();
  }
  closeTool() {
    if (this.isToolOpen) this.viewState.closeTool();
    super.deactivate();
  }
}, _applyDecoratedDescriptor(_class.prototype, "isToolOpen", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "isToolOpen"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "active", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "active"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "closeTool", [action], Object.getOwnPropertyDescriptor(_class.prototype, "closeTool"), _class.prototype), _class);